import { PACKAGE_TYPE, PurchasesOffering, PurchasesPackage, PurchasesPlugin } from '@revenuecat/purchases-capacitor';
import {
    CustomerState,
    EntitlementId,
    normalizeCustomer,
    OfferingId,
    PackageFrequency,
    PackageSummary,
    RCAdapter,
} from './RcAdapter.ts';
import { Capacitor } from '@capacitor/core';
import { UserCancellationError } from './utils.ts';
import { Mutex } from 'async-mutex';
import { RawLocalState } from './State.tsx';
import { Session } from '@supabase/supabase-js';

const LAST_APP_USER_ID_KEY = 'rc_last_app_user_id';

/** =========================================================
 *  Native (Capacitor: StoreKit / Play Billing)
 *  Picks iOS or Android key internally.
 *  =======================================================*/
export class RcNative implements RCAdapter {
    private purchases: PurchasesPlugin;
    private apiKey: string;
    private configuredFor?: string; // appUserId
    private mutex = new Mutex();
    private configurationPromise: Promise<void>;

    constructor({
        iosKey,
        androidKey,
        initialState,
        Purchases,
    }: {
        iosKey: string;
        androidKey: string;
        initialState: RawLocalState;
        Purchases: PurchasesPlugin | null;
    }) {
        const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
        if (platform === 'ios') {
            if (!iosKey) throw new Error('RevenueCat iOS key is required');
            this.apiKey = iosKey;
        } else if (platform === 'android') {
            if (!androidKey) throw new Error('RevenueCat Android key is required');
            this.apiKey = androidKey;
        } else {
            throw new Error('RCUnifiedNative constructed on non-native platform');
        }
        if (Purchases == null) {
            throw new Error('Purchases is not available on this platform');
        }
        this.purchases = Purchases;
        const session: Session | null = initialState.session ? (JSON.parse(initialState.session) as Session) : null;
        // We need the ID at this point just to preview the packages available.
        // This is not necessarily the final user ID we will use for purchases.
        // First we try to use the logged in user ID. Otherwise we fall back to
        // the last known user ID, even if the user is not actually signed in
        // with this ID right now.
        //
        // We do this to avoid "aliasing" in RevenueCat, which happens when
        // calling RC's logIn method _from_ an anonymous ID.  This associates
        // the anonymous ID with the user's account as an "alias", and there are
        // a finite number of aliases allowed per user in RC.
        // To avoid this, we try to reuse the last known user ID if possible.
        //
        // For now we just use localStorage because it's synchronous.  This was
        // the recommended approach from RC support. In the future we might want
        // to use the KeyValueStore instead, which will just require an async
        // init() method.
        const userId: string | undefined = session?.user.id ?? localStorage.getItem(LAST_APP_USER_ID_KEY) ?? undefined;
        this.configurationPromise = Purchases.configure({
            apiKey: this.apiKey,
            ...(userId && { appUserID: userId }),
        }).then(() => {
            this.configuredFor = userId ?? 'anonymous';
        });
    }

    private async ensureConfigured() {
        await this.configurationPromise;
    }

    private async ensureLoggedInAsUser(appUserId: string) {
        if (this.configuredFor === appUserId) return;
        await this.ensureConfigured();
        await this.purchases.logIn({ appUserID: appUserId });
        // Cache the appUserId so we don't need to use an anonymous ID again
        localStorage.setItem(LAST_APP_USER_ID_KEY, appUserId);
        this.configuredFor = appUserId;
    }

    async getSubscriptionManagementUrl(appUserId: string): Promise<string | null> {
        await this.ensureLoggedInAsUser(appUserId);
        const info = await this.purchases.getCustomerInfo();
        return info.customerInfo.managementURL;
    }

    async hasEntitlement(appUserId: string, entitlementId: EntitlementId, useCache: boolean = true) {
        return this.mutex.runExclusive(async () => {
            await this.ensureLoggedInAsUser(appUserId);
            if (!useCache) {
                await this.purchases.invalidateCustomerInfoCache();
            }
            const info = await this.purchases.getCustomerInfo();
            return Boolean(info.customerInfo.entitlements.active?.[entitlementId]);
        });
    }

    private toFrequency(packageType: PACKAGE_TYPE): PackageFrequency {
        switch (packageType) {
            case PACKAGE_TYPE.MONTHLY:
                return 'monthly';
            case PACKAGE_TYPE.ANNUAL:
                return 'annual';
            default:
                return 'unknown';
        }
    }

    async getPackages(offeringId?: OfferingId) {
        return this.mutex.runExclusive(async () => {
            await this.ensureConfigured();
            const offerings = await this.purchases.getOfferings();
            const current: PurchasesOffering | null =
                offeringId && offerings.all?.[offeringId] ? offerings.all[offeringId] : offerings.current;
            if (!current) return [];
            return current.availablePackages.map((pkg: PurchasesPackage) => {
                return {
                    title: pkg.product.title,
                    frequency: this.toFrequency(pkg.packageType),
                    price: {
                        amount: pkg.product.price,
                        currencyCode: pkg.product.currencyCode,
                        localized: pkg.product.priceString,
                    },
                    id: pkg.identifier,
                    _impl: pkg,
                };
            });
        });
    }

    async purchase(appUserId: string, pkg: PackageSummary): Promise<CustomerState> {
        return this.mutex.runExclusive(async () => {
            try {
                await this.ensureLoggedInAsUser(appUserId);
                const { customerInfo } = await this.purchases.purchasePackage({ aPackage: pkg._impl }); // opens StoreKit/Play sheet

                return normalizeCustomer(customerInfo);
            } catch (e) {
                // For now assume all errors are user cancellation errors
                throw new UserCancellationError('User cancelled purchase');
            }
        });
    }

    async restore(appUserId: string): Promise<CustomerState> {
        return this.mutex.runExclusive(async () => {
            try {
                await this.ensureLoggedInAsUser(appUserId);
                const { customerInfo } = await this.purchases.restorePurchases();
                return normalizeCustomer(customerInfo);
            } catch (e) {
                console.log('Ran into a problem running restore: ', e);
                // For now assume all errors are user cancellation errors
                throw new UserCancellationError('User cancelled restore');
            }
        });
    }
}
